/**
 * Hook for detecting and managing custom domain tenant context.
 * 
 * On app initialization, this hook checks if the current hostname is a custom domain
 * and retrieves the associated tenant (user) information.
 */

import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/features/help/utils/logger';
import { getApiOrigin } from '@/features/api/utils/api-origin.util';

interface TenantContext {
  isCustomDomain: boolean;
  userShortId: string | null;
  userId: string | null;
  hostname: string;
  isLoading: boolean;
  error: string | null;
}

const STANDARD_DOMAINS = [
  'apply.ahisign.com',
  'dev.ahisign.com',
  'localhost',
  '127.0.0.1',
];

/**
 * Checks if the current hostname is a standard (non-custom) domain.
 */
const isStandardDomain = (hostname: string): boolean => {
  const host = hostname.split(':')[0];
  return STANDARD_DOMAINS.some(domain => 
    host === domain || host.endsWith(`.${domain}`)
  );
};

/**
 * Hook to detect and manage custom domain tenant context.
 * 
 * Usage:
 * ```tsx
 * const { isCustomDomain, userShortId, isLoading } = useTenantContext();
 * ```
 */
export const useTenantContext = (): TenantContext => {
  const [context, setContext] = useState<TenantContext>({
    isCustomDomain: false,
    userShortId: null,
    userId: null,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    isLoading: true,
    error: null,
  });

  const detectTenant = useCallback(async () => {
    const hostname = window.location.hostname;
    
    logger.debug('TenantContext', 'Detecting tenant for hostname', { hostname });

    // Quick check for standard domains - no API call needed
    if (isStandardDomain(hostname)) {
      logger.debug('TenantContext', 'Standard domain detected, no tenant lookup needed');
      setContext({
        isCustomDomain: false,
        userShortId: null,
        userId: null,
        hostname,
        isLoading: false,
        error: null,
      });
      return;
    }

    // Custom domain - call the backend tenant lookup API directly
    try {
      logger.info('TenantContext', 'Custom domain detected, looking up tenant', { hostname });
      
      const apiOrigin = getApiOrigin();
      const response = await fetch(
        `${apiOrigin}/api/tenants/lookup?domain=${encodeURIComponent(hostname)}`
      );
      
      if (!response.ok) {
        throw new Error(`Tenant lookup failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || data.error) {
        logger.warn('TenantContext', 'Tenant lookup returned error', { 
          hostname, 
          error: data.error 
        });
        setContext({
          isCustomDomain: true,
          userShortId: null,
          userId: null,
          hostname,
          isLoading: false,
          error: data.error || 'Domain not found or not verified',
        });
        return;
      }

      logger.info('TenantContext', 'Tenant found for custom domain', {
        hostname,
        userShortId: data.userShortId,
      });

      setContext({
        isCustomDomain: true,
        userShortId: data.userShortId,
        userId: data.userId,
        hostname,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('TenantContext', 'Error detecting tenant', { 
        hostname, 
        error: errorMessage 
      });
      setContext({
        isCustomDomain: true,
        userShortId: null,
        userId: null,
        hostname,
        isLoading: false,
        error: errorMessage,
      });
    }
  }, []);

  useEffect(() => {
    detectTenant();
  }, [detectTenant]);

  return context;
};
